/**
 * roomNumberList — generates the candidate room numbers for one
 * building, based on its floor_count.
 *
 * Universal dorm shape: floors numbered 1..floor_count, 24 doors per
 * floor, room number = floor*100 + offset (101..1224 etc.). Cleaner
 * picks the number off the door; the picker offers every plausible one
 * for the building. Numbers that aren't real rooms get no claim and
 * never enter the DB.
 */

export const ROOMS_PER_FLOOR = 24

/** Largest plausible floor count for a dorm building (sanity bound). */
export const MAX_FLOOR_CEILING = 30

/**
 * Extra floors offered above the building's declared floor_count, so a
 * cleaner who finds an undeclared floor can still pick the room without
 * an admin having to extend the building first. The Edge Function
 * accepts the higher number and auto-bumps building.floor_count, with
 * the same MAX_FLOOR_CEILING as a hard ceiling.
 */
export const PICKER_FLOOR_BUFFER = 1

/**
 * Floor count the picker offers when the building has no declared
 * floor_count (NULL or 0). Sized for ABC's pre-go-live state on
 * 2026-05-18 — the school has not released a floor manifest, so
 * cleaners discover the building shape one room at a time. 4 floors
 * × 24 rooms = 96 options keeps the dropdown scrollable while
 * covering the typical dorm shape; cleaners on a taller building can
 * switch the picker into manual entry mode.
 */
export const DEFAULT_DISCOVERY_FLOORS = 4

export function generateRoomNumbers(floorCount: number | null): string[] {
  if (floorCount == null || floorCount < 1) return []
  const safeFloors = Math.min(Math.max(floorCount, 1), MAX_FLOOR_CEILING)
  const out: string[] = []
  for (let floor = 1; floor <= safeFloors; floor++) {
    for (let offset = 1; offset <= ROOMS_PER_FLOOR; offset++) {
      out.push(String(floor * 100 + offset))
    }
  }
  return out
}

/**
 * floor_count plus the discovery buffer, capped at MAX_FLOOR_CEILING.
 * When the building has no declared floor_count (NULL/0), returns
 * DEFAULT_DISCOVERY_FLOORS so the picker is still usable while the
 * building is being discovered. The SQL claim-room RPC matches:
 * first-claim against a NULL-floor_count building accepts any floor
 * up to MAX_FLOOR_CEILING (see migration 20260515000001).
 */
export function pickerFloorCount(declaredFloorCount: number | null): number {
  if (declaredFloorCount == null || declaredFloorCount < 1) return DEFAULT_DISCOVERY_FLOORS
  return Math.min(MAX_FLOOR_CEILING, declaredFloorCount + PICKER_FLOOR_BUFFER)
}
